export type ConversationFilter = 'all' | 'active' | 'completed' | 'failed' | 'archived'

export interface ConversationNavItem {
  id: string
  project_id: string
  connection_id?: string | null
  connections?: {
    id: string
    name: string
    connector_family: string
    status: 'draft' | 'saved' | 'deployed' | 'failed' | 'archived'
  } | null
  title: string
  pinned: boolean
  archived: boolean
  lifecycle_status: 'active' | 'completed' | 'failed'
  created_at: string
  updated_at: string
  messages?: Array<{ count: number }>
}

export interface ConversationListPage {
  conversations: ConversationNavItem[]
  page: { has_more: boolean; next_offset: number | null }
}

export function normalizeConversationListPage(value: unknown): ConversationListPage {
  if (Array.isArray(value)) {
    return { conversations: value as ConversationNavItem[], page: { has_more: false, next_offset: null } }
  }
  const response = value as Partial<ConversationListPage> | null
  return {
    conversations: Array.isArray(response?.conversations) ? response.conversations : [],
    page: {
      has_more: Boolean(response?.page?.has_more),
      next_offset: typeof response?.page?.next_offset === 'number' ? response.page.next_offset : null,
    },
  }
}

export function sortConversations(items: ConversationNavItem[]) {
  return [...items].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  })
}

export function mergeConversationPages(current: ConversationNavItem[], incoming: ConversationNavItem[]) {
  const byId = new Map(current.map(item => [item.id, item]))
  incoming.forEach(item => byId.set(item.id, item))
  return sortConversations(Array.from(byId.values()))
}

export function filterConversations(items: ConversationNavItem[], search: string, filter: ConversationFilter) {
  const query = search.trim().toLowerCase()
  return items.filter(item => {
    const matchesSearch = !query || `${item.title || 'New conversation'} ${item.connections?.name || ''}`.toLowerCase().includes(query)
    const matchesFilter = filter === 'archived'
      ? item.archived
      : !item.archived && (filter === 'all' || item.lifecycle_status === filter)
    return matchesSearch && matchesFilter
  })
}

export function conversationTime(value?: string, now = Date.now()) {
  if (!value) return 'No activity'
  const timestamp = new Date(value).getTime()
  if (!Number.isFinite(timestamp)) return 'Unknown'
  const minutes = Math.max(0, Math.floor((now - timestamp) / 60000))
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`
  if (minutes < 10080) return `${Math.floor(minutes / 1440)}d ago`
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(value))
}

export function automaticConversationTitle(currentTitle: string | null | undefined, firstMessage: string) {
  if (currentTitle?.trim()) return null
  const message = firstMessage.trim()
  if (!message) return null
  return message.slice(0, 50) + (message.length > 50 ? '...' : '')
}
